import { useTexture } from '@react-three/drei'
import { useEffect, useMemo } from 'react'
import { MathUtils, Object3D } from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import type { BallastInstanceData } from '@hooks'
import { BALLAST_PLANE_GEOMETRY, BALLAST_Y, getBallastMaterial, TEXTURE_PATHS } from '@utils'

interface Props {
  instances: BallastInstanceData[]
}

export function BallastMergedManager({ instances }: Props) {
  const textures = useTexture(TEXTURE_PATHS)

  const groups = useMemo(() => {
    const map = new Map<string, BallastInstanceData[]>()
    for (const inst of instances) {
      const arr = map.get(inst.textureName) ?? []
      arr.push(inst)
      map.set(inst.textureName, arr)
    }

    return Array.from(map.entries()).map(([texName, group]) => {
      const obj = new Object3D()
      const geos = group.map(({ worldPos, rotationDeg, scale, positionYOffset }) => {
        obj.position.set(worldPos.x, BALLAST_Y + (positionYOffset ?? 0), worldPos.z)
        obj.rotation.set(MathUtils.degToRad(-90), 0, MathUtils.degToRad(rotationDeg))
        obj.scale.set(1, scale ?? 1, 1)
        obj.updateMatrix()
        return BALLAST_PLANE_GEOMETRY.clone().applyMatrix4(obj.matrix)
      })

      const geometry = mergeGeometries(geos)
      geos.forEach(g => g.dispose())
      return { texName, geometry }
    })
  }, [instances])

  useEffect(() => {
    return () => {
      groups.forEach(({ geometry }) => geometry?.dispose())
    }
  }, [groups])

  return (
    <>
      {groups.map(({ texName, geometry }) => (
        <mesh
          key={texName}
          receiveShadow
          geometry={geometry}
          material={getBallastMaterial(textures[texName])}
        />
      ))}
    </>
  )
}
